/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  NavDirection,
  addSortingAttributes,
  calcDistanceAngleWeighted,
  calcInContact,
  getLinkComparator,
  getNavigableLinksForNodeId,
  isValidDestination
} from '../utils/navUtils';
import type {
  DiagramNodeData,
  DiagramLinkData,
  Bounds,
  ItemInfo,
  PromotedLinkId
} from '../diagram.types';
import { useRef } from 'preact/hooks';
import { getPromotedString, isPromoted } from '../utils/diagramUtils';

export function useDiagramNavigation<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  bounds: Map<K1, Bounds>,
  linkIdToDataMap: Map<K2, D2>,
  promotedLinkMap: Map<string, PromotedLinkId<K1, D2>>,
  nodeIdToDataMap: Map<K1, D1>
) {
  const currentKeyboardFocusNode = useRef<K1>();
  const nodeBoundsById = bounds;

  function getNextNavigableNode(
    direction: NavDirection,
    compareCenters: boolean,
    current?: D1,
    listOfObjects?: D1[]
  ) {
    if (!listOfObjects) return current;

    if (!current) return listOfObjects[0];
    const currBB = nodeBoundsById.get(current.id);
    if (!currBB) return listOfObjects[0];

    let nextObject = current; //init to current object
    let nextDistance = Number.MAX_VALUE;

    // If an object is in contact it overrules all other attributes
    // Only another in contact object with better attributes will have higher precedence
    let nextInContact = false;

    // Whether or not the for loop has encountered the current object
    let hasFoundCurrent = false;

    let object, inContact, distance;
    for (let i = 0; i < listOfObjects.length; i++) {
      object = listOfObjects[i];

      if (object === current) {
        hasFoundCurrent = true;
        continue;
      }
      const objBB = nodeBoundsById.get(object.id);
      if (!objBB) continue;

      const objBounds = { x: objBB.x, y: objBB.y, width: objBB.width, height: objBB.height };
      const currBounds = { x: currBB.x, y: currBB.y, width: currBB.width, height: currBB.height };
      if (!isValidDestination(objBounds, currBounds, direction, compareCenters)) continue;

      inContact = calcInContact(objBounds, currBounds, direction);

      if (nextInContact && !inContact) continue;

      distance = calcDistanceAngleWeighted(objBounds, currBounds, direction);

      if (distance == 0 && !hasFoundCurrent) continue;

      // Make sure incontact flag have highest precedence
      if (
        (!nextInContact && inContact) ||
        (distance < nextDistance && ((nextInContact && inContact) || !nextInContact))
      ) {
        nextDistance = distance;
        nextObject = object;
        nextInContact = inContact;
      }
    }
    return nextObject;
  }

  /**
   * Get next navigavle link depending on direction - clockwise or conter clockwise.
   * The decision is made based on location of nodes centers rather than link paths or link angles.
   */
  function getNextNavigableLink(
    direction: string,
    listOfNodes: D1[],
    currentLink?: D2 | PromotedLinkId<K1, D2>,
    listOfLinks?: D2[],
    promotedLinks?: PromotedLinkId<K1, D2>[]
  ) {
    if (!(listOfLinks || promotedLinks)) return currentLink;
    const allLinks =
      listOfLinks && promotedLinks
        ? [...listOfLinks, ...promotedLinks]
        : (listOfLinks ? listOfLinks : promotedLinks)!;

    if (!currentLink) return allLinks[0];
    const keyboardFocusNode = currentKeyboardFocusNode.current;
    const nodeId = keyboardFocusNode ? keyboardFocusNode : currentLink.startNode;
    const node = nodeIdToDataMap.get(nodeId);

    if (!node) return currentLink;
    const adjLinks = getNavigableLinksForNodeId<K1, K2, D2>(node.id, allLinks);
    const linksWithSortingAttributes = addSortingAttributes<K1, K2, D1, D2>(
      node,
      adjLinks,
      listOfNodes,
      nodeBoundsById
    );
    linksWithSortingAttributes.sort(getLinkComparator());
    //clockwise direction
    const bForward = direction == 'down' ? true : false;
    let index = 0;
    for (let i = 0; i < linksWithSortingAttributes.length; i++) {
      const id = 'id' in currentLink ? currentLink.id : getPromotedString(currentLink);
      const link = linksWithSortingAttributes[i];
      if (link.id === id) {
        if (bForward) index = i == linksWithSortingAttributes.length - 1 ? 0 : i + 1;
        else index = i == 0 ? linksWithSortingAttributes.length - 1 : i - 1;
        break;
      }
    }
    if (linksWithSortingAttributes[index].type === 'link') {
      return linkIdToDataMap.get(linksWithSortingAttributes[index].id as K2);
    }
    return promotedLinkMap.get(linksWithSortingAttributes[index].id as string);
  }

  /**
   * Returns the link detail when a node to link navigation occurs
   */
  function navigateFromNodeToLink(
    listOfLinks: D2[],
    promotedLinks: PromotedLinkId<K1, D2>[],
    event: KeyboardEvent,
    node?: D1
  ): ItemInfo<K1, K2, D2> | undefined {
    if (!node) return undefined;
    const adjLinks = getNavigableLinksForNodeId(node.id, [...listOfLinks, ...promotedLinks]);
    if (adjLinks.length < 1) return undefined;
    let link = adjLinks[0];
    const nodeBB = nodeBoundsById.get(node.id)!;
    let object, currNode, currNodeBB, currNodeBBCenterX;
    const nodeCenterX = nodeBB.x + nodeBB.width / 2;
    currentKeyboardFocusNode.current = node.id;
    for (let i = 0; i < adjLinks.length; i++) {
      object = adjLinks[i];
      currNode = object.startNode === node.id ? object.endNode : object.startNode;
      currNodeBB = nodeBoundsById.get(currNode);
      currNodeBBCenterX = currNodeBB!.x + currNodeBB!.width / 2;
      if (
        // equivalent of ≤ and ≥
        (event.altKey && event.code === 'Comma' && currNodeBBCenterX <= nodeCenterX) ||
        (event.altKey && event.code === 'Period' && currNodeBBCenterX >= nodeCenterX)
      ) {
        link = object;
        break;
      }
    }
    return (
      isPromoted(link) ? { id: link, type: 'promotedLink' } : { id: (link as D2).id, type: 'link' }
    ) as ItemInfo<K1, K2, D2>;
  }

  /**
   * Returns the node detail when a link to node navigation occurs
   */
  function navigateFromLinkToNode(
    startNode: K1,
    endNode: K1,
    event: KeyboardEvent
  ): ItemInfo<K1, K2, D2> | undefined {
    const key = event.key;
    const startNodeBB = nodeBoundsById.get(startNode);
    const endNodeBB = nodeBoundsById.get(endNode);
    if (!startNodeBB || !endNodeBB) return undefined;
    const startNodeCenterX = startNodeBB.x + startNodeBB.width / 2;
    const endNodeCenterX = endNodeBB.x + endNodeBB.width / 2;
    const newNode =
      (endNodeCenterX > startNodeCenterX && key === 'ArrowRight') ||
      (endNodeCenterX < startNodeCenterX && key === 'ArrowLeft')
        ? endNode
        : startNode;
    return { id: newNode, type: 'node' };
  }

  return {
    getNextNavigableNode,
    getNextNavigableLink,
    navigateFromNodeToLink,
    navigateFromLinkToNode
  };
}
